import {
  faCircleInfo,
  faFileArrowDown,
  faFileCircleXmark,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { memo, useEffect, useRef, useState } from "react";
import {
  Badge,
  Col,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { formatBytes } from "shared/utils";
import { Button } from "ui";
import "./FormMultiFileInput.styles.css";

/**
 * @deprecated use src/shared/ui/FilesInput instead
 */
function MultiFileInput({
  name,
  attachedFiles = [],
  acceptedExtensions = "",
  toDisplay = false,
  disabled = false,
  maxNumberOfFiles = 1,
  maxAllowedFileSize = 2097152, // 2MB'
  ...props
}) {
  const filesRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [showPopover, setShowPopover] = useState({ show: false, content: "" });
  const [showDependenciesPopover, setShowDependenciesPopover] = useState(false);
  const [removalConfirmation, setRemovalConfirmation] = useState({
    file_name: "",
    canBeRemoved: false,
  });
  const { t } = useTranslation(["CostLine", "Shared"]);
  const UpdatingPopover = React.forwardRef(
    // @ts-ignore
    ({ popper, children, show: _, ...props }, ref) => {
      useEffect(() => {
        popper.scheduleUpdate();
      }, [children, popper]);

      return (
        <Popover ref={ref} body {...props}>
          {children}
        </Popover>
      );
    }
  );

  const {
    setValue,
    formState: { errors },
  } = useFormContext();

  const dataTransferRef = useRef(new DataTransfer());

  const handleFileChange = (e) => {
    if (!e.target.files.length) {
      filesRef.current.files = dataTransferRef.current.files;
      return;
    }
    Array.from(e.target.files).forEach((file) => {
      const fileIsUploaded = files?.some(
        (f) =>
          f?.file?.name === file.name ||
          (f?.original_name === file.name && f?.action !== "deleteAttachment")
      );
      if (fileIsUploaded) {
        setShowPopover({
          show: true,
          content: t("attachments.popovers.validation_duplicate", {
            ns: "Shared",
            fileName: file.name,
          }),
        });
        return;
      }
      const deletedAttachments = files.filter(
        (file) => file?.action === "deleteAttachment"
      );
      if (
        dataTransferRef.current.items.length +
          attachedFiles.length -
          deletedAttachments.length >=
        maxNumberOfFiles
      ) {
        setShowPopover({
          show: true,
          content: t("attachments.popovers.validation_count", {
            count: maxNumberOfFiles,
            ns: "Shared",
          }),
        });
        return;
      }
      setShowPopover({ show: false, content: "" });
      dataTransferRef.current.items.add(file);
    });

    filesRef.current.files = dataTransferRef.current.files;
    const storedFiles = files?.filter((file) => file?.original_name);
    const filesToSave = Array.from(filesRef.current.files).map((file) => {
      const stored = storedFiles.find((f) => f?.original_name === file.name);
      if (stored) {
        return {
          file,
          id: stored?.id,
          action: "updateAttachment",
        };
      } else {
        return {
          file,
          id: `fileId${Math.random()}`.replace(".", "").slice(0, 20),
          action: "storeAttachment",
        };
      }
    });
    setFiles([...storedFiles, ...filesToSave]);
    setValue(name, [...storedFiles, ...filesToSave], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (attachedFiles.length && !toDisplay) {
      dataTransferRef.current.clearData();
      filesRef.current.files = dataTransferRef.current.files;
      setFiles(attachedFiles);
      setValue(name, attachedFiles, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [attachedFiles, toDisplay]);

  const handleFileRemoval = (fileName) => {
    Array.from(dataTransferRef.current.files).forEach((file, idx) => {
      if (file.name === fileName) {
        dataTransferRef.current.items.remove(idx);
      }
    });
    filesRef.current.files = dataTransferRef.current.files;
    setFiles((prev) => prev.filter(({ file }) => file?.name !== fileName));

    setValue(
      name,
      files.filter(({ file }) => file?.name !== fileName),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
  };

  const handleStoredFileRemoval = (fileName) => {
    setFiles((prev) =>
      prev.map((file) => {
        if (file?.original_name === fileName) {
          return {
            ...file,
            action: "deleteAttachment",
          };
        }
        return file;
      })
    );
    setValue(
      name,
      files?.map((file) => {
        if (file?.original_name === fileName) {
          return {
            ...file,
            action: "deleteAttachment",
          };
        }
        return file;
      }),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
  };
  return (
    <>
      {/* // TODO Modal decomposition */}
      <Modal
        show={removalConfirmation.canBeRemoved}
        onHide={() =>
          setRemovalConfirmation({ file_name: "", canBeRemoved: false })
        }
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("attachments.file_removal", { ns: "Shared" })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("attachments.removal_warning", {
            fileName: removalConfirmation.file_name,
            ns: "Shared",
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() =>
              setRemovalConfirmation({ file_name: "", canBeRemoved: false })
            }
          >
            {t("buttons.cancel", { ns: "Shared" })}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setRemovalConfirmation({ file_name: "", canBeRemoved: false });
              handleStoredFileRemoval(removalConfirmation.file_name);
            }}
          >
            {t("buttons.delete", { ns: "Shared" })}
          </Button>
        </Modal.Footer>
      </Modal>
      <Controller
        name={name}
        render={({ field }) => (
          <Form.Group as={Col} controlId="Attachments">
            <Form.Label>{t("labels.attachments")}</Form.Label>
            {toDisplay ? (
              attachedFiles.length ? (
                <div className="d-flex gap-1 mt-1 flex-wrap">
                  {attachedFiles?.map((attachment) => {
                    return (
                      <a
                        key={attachment.id}
                        href={attachment.download_link}
                        target="_blank"
                      >
                        <Badge
                          bg="primary"
                          pill
                          className="me-2 files-input__badge"
                        >
                          <div className="text-wrap text-break">
                            {attachment.original_name}
                          </div>
                          <FontAwesomeIcon
                            className="ms-3"
                            icon={faFileArrowDown}
                          />
                        </Badge>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-2">
                  <FontAwesomeIcon
                    className="ms-3"
                    style={{ height: "0.9rem" }}
                    icon={faFileCircleXmark}
                  />
                  <small className="ms-2">
                    {t("attachments.no_files_selected", { ns: "Shared" })}
                  </small>
                </div>
              )
            ) : (
              <>
                <InputGroup hasValidation>
                  <OverlayTrigger
                    show={showPopover.show}
                    onToggle={() =>
                      setShowPopover((state) => ({ ...state, show: false }))
                    }
                    delay={2000}
                    overlay={
                      <UpdatingPopover id="popover-contained">
                        {showPopover.content}
                      </UpdatingPopover>
                    }
                  >
                    <div
                      className={`files-input ${
                        errors?.attachments ? "is-invalid" : ""
                      }`}
                      aria-disabled={disabled}
                    >
                      <p>{t("add_attachment", { ns: "Shared" })}</p>
                      <Form.Control
                        onChange={handleFileChange}
                        // @ts-ignore
                        onClick={(e) => (e.target.value = null)}
                        ref={filesRef}
                        type="file"
                        isInvalid={!!errors?.[name]}
                        multiple
                        disabled={disabled}
                        accept={
                          /Android\s14/i.test(navigator.userAgent)
                            ? // https://stackoverflow.com/questions/77886145/chrome-on-android-14-does-not-show-the-option-to-open-the-camera-when-uploading
                              "image/*,text/plain"
                            : acceptedExtensions
                        }
                        {...props}
                      />
                    </div>
                  </OverlayTrigger>
                  <OverlayTrigger
                    show={showDependenciesPopover}
                    onToggle={() => setShowDependenciesPopover(false)}
                    delay={2000}
                    overlay={
                      <Popover body>
                        {t("attachments.popovers.validation", {
                          count: maxNumberOfFiles,
                          size: formatBytes(maxAllowedFileSize),
                          formats: acceptedExtensions,
                          ns: "Shared",
                        })}
                      </Popover>
                    }
                  >
                    <InputGroup.Text
                      as="button"
                      type="button"
                      onClick={(e) => setShowDependenciesPopover(true)}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="text-muted"
                      />
                    </InputGroup.Text>
                  </OverlayTrigger>

                  <Form.Control.Feedback type="invalid">
                    {errors?.[name]?.message.toString()}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text>
                  <div className="mt-2">
                    {files?.map((file) => {
                      return (
                        file?.action !== "deleteAttachment" && (
                          <Badge
                            pill
                            className="me-2 files-input__badge"
                            bg={file?.original_name ? "primary" : "secondary"}
                            key={Math.random()}
                          >
                            {file?.file?.name ? (
                              <div className="text-wrap text-break">
                                {file?.file?.name}
                              </div>
                            ) : (
                              <a
                                target="_blank"
                                href={file.download_link}
                                className="text-white text-wrap text-break "
                              >
                                {file.original_name}
                              </a>
                            )}
                            <>
                              {!disabled && (
                                <button
                                  type="button"
                                  style={{ all: "unset", cursor: "pointer" }}
                                  onClick={() =>
                                    file?.original_name
                                      ? setRemovalConfirmation({
                                          file_name: file?.original_name,
                                          canBeRemoved: true,
                                        })
                                      : handleFileRemoval(file?.file.name)
                                  }
                                >
                                  <FontAwesomeIcon
                                    className="ms-3"
                                    icon={faXmark}
                                  />
                                </button>
                              )}
                            </>
                          </Badge>
                        )
                      );
                    })}
                  </div>
                </Form.Text>
              </>
            )}
          </Form.Group>
        )}
      />
    </>
  );
}

export default memo(MultiFileInput);
